import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, Button, ChevronDownIcon, Flex, IconButton, Text } from "@radix-ui/themes";
import {
  CheckIcon,
  ExitIcon,
  GearIcon,
  MoonIcon,
  SunIcon,
  PlusIcon,
  DoubleArrowLeftIcon,
} from "@radix-ui/react-icons";
import cx from "classnames";
import styles from "./styles.module.scss";
import { ThemeContext } from "@/components/Theme";
import { useGetAccess } from "@/api/useAccess";
import { FeatureAccessContext } from "@/context/featureAccess";
import DropdownMenu, {
  DropdownMenuProps,
} from "../shared/dropdown-menu/DropdownMenu";
import { ShieldIcon } from "lucide-react";
import { isQuack } from "@/v2/components/feature-flag/FeatureFlagContext";
import { posthogTrackEvent, AuthEvents } from "@/assets/posthog";
import { CreateWorkspaceDialog } from "@/components/workspace/CreateWorkspaceDialog";
import { Tooltip } from "@/components/shared/tooltip";
import { getInitials } from "@/v2/assets/utils";

interface AppMenuProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function AppMenu({ isCollapsed, onToggleCollapse }: AppMenuProps) {
  const navigate = useNavigate();
  const { logout, user } = useAuth0();
  const { data: getAccessData, refetch: refetchAccess } = useGetAccess(!!user);
  const { selectedWorkspace, setSelectedWorkspace } =
    useContext(FeatureAccessContext);
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const [showCreateWorkspaceDialog, setShowCreateWorkspaceDialog] =
    useState(false);

  const menuItems = useMemo<DropdownMenuProps["items"]>(() => {
    const isQuackUser = isQuack(user);

    const workspaceItems = [
      ...(getAccessData?.tenants?.map((t: string) => ({
        label: t,
        icon: selectedWorkspace === t ? <CheckIcon /> : undefined,
        onClick: () => setSelectedWorkspace(t),
      })) || []),
      {
        type: "separator" as const,
      },
      {
        label: "Create New Workspace",
        icon: <PlusIcon />,
        onClick: () => setShowCreateWorkspaceDialog(true),
      },
    ];

    return [
      {
        label: "Choose your workspace",
        items: workspaceItems,
      },
      {
        type: "separator",
      },
      {
        label: "Settings",
        icon: <GearIcon />,
        onClick: () => navigate("/settings"),
      },
      {
        label: "Switch mode",
        icon: isDarkMode ? <SunIcon /> : <MoonIcon />,
        onClick: () => toggleTheme(),
      },
      {
        type: "separator",
      },
      ...(isQuackUser
        ? [
            {
              label: "Admin Dashboard",
              icon: <ShieldIcon size={16} />,
              onClick: () => navigate("/admin-dashboard"),
            },
          ]
        : []),
      {
        label: "Logout",
        icon: <ExitIcon />,
        onClick: () => {
          posthogTrackEvent({
            event: AuthEvents.LOGOUT,
            properties: {
              reason: "user_initiated",
              timestamp: new Date().toISOString(),
            },
          });
          logout({
            logoutParams: { returnTo: window.location.origin },
          });
        },
      },
    ];
  }, [
    getAccessData,
    selectedWorkspace,
    setSelectedWorkspace,
    showCreateWorkspaceDialog,
  ]);

  const handleWorkspaceCreated = useCallback(async () => {
    await refetchAccess();
  }, [refetchAccess]);

  useEffect(() => {
    if (getAccessData && getAccessData.tenants?.[0] && !selectedWorkspace) {
      setSelectedWorkspace(getAccessData.tenants[0]);
    }
  }, [JSON.stringify(getAccessData)]);

  const displayWorkspace = selectedWorkspace || user?.owner || "";
  const workspaceInitials = useMemo(
    () => (displayWorkspace ? getInitials(displayWorkspace) : ""),
    [displayWorkspace]
  );

  return (
    <>
      <Flex
        width="100%"
        align="center"
        py="2"
        px="1"
        direction="row"
        justify={isCollapsed ? "center" : "between"}
      >
        {!isCollapsed && (
          <DropdownMenu items={menuItems}>
            <DropdownMenu.Trigger>
              <Button
                color="gray"
                variant="ghost"
                size="2"
                className={styles.workspaceButton}
              >
                <Flex gap="2" align="center" className={styles.workspaceButtonContent}>
                  <Avatar
                    size="1"
                    fallback={workspaceInitials}
                    color="gray"
                    className={styles.workspaceAvatar}
                  />
                  {displayWorkspace ? <Text>{displayWorkspace}</Text> : null}
                </Flex>
                <ChevronDownIcon />
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        )}
        <Tooltip>
          <Tooltip.Trigger>
            <IconButton
              variant="ghost"
              color="gray"
              size="2"
              onClick={onToggleCollapse}
              className={cx(styles.collapseToggle, {
                [styles.collapsed]: isCollapsed,
              })}
            >
              <DoubleArrowLeftIcon />
            </IconButton>
          </Tooltip.Trigger>
          <Tooltip.Content side="right">
            {isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          </Tooltip.Content>
        </Tooltip>
      </Flex>

      <CreateWorkspaceDialog
        open={showCreateWorkspaceDialog}
        onOpenChange={setShowCreateWorkspaceDialog}
        onWorkspaceCreated={handleWorkspaceCreated}
      />
    </>
  );
}
